import React from "react";
import "./shared-Comp.css";

const ProfileHelpComp = () => {
  return (
    <div className="min-h-screen bg-gray-100 dark:bg-stone-900 p-4">
      <div className="bg-white dark:bg-stone-800 rounded-lg shadow-md p-6">
        <h1 className="text-2xl fontKanitRegular font-medium mb-4 text-center text-gray-900 dark:text-stone-100">
          Help & Support
        </h1>
        <p className="mb-4 fontAlbertSansRegular text-gray-700 dark:text-stone-300">
          Welcome to KeyWay Help & Support. Here you’ll find answers to the most
          common questions about using the app, your subscription, and accessing
          our audiobook library.
        </p>

        {/* Getting Started */}
        <div className="mb-6">
          <h2 className="text-xl fontKanitRegular font-medium mb-2 text-gray-900 dark:text-stone-100">
            Getting Started
          </h2>
          <ul className="list-disc pl-5 fontAlbertSansRegular text-gray-700 dark:text-stone-300">
            <li className="mb-2">
              <strong className="text-gray-800 dark:text-stone-200">
                How do I create an account?
              </strong>
              <p>
                Download the KeyWay app, then sign up using your email or social
                login. Follow the prompts to set up your profile.
              </p>
            </li>
            <li className="mb-2">
              <strong className="text-gray-800 dark:text-stone-200">
                How do I reset my password?
              </strong>
              <p>
                On the login screen, tap <em>Forgot Password</em>. Enter your
                registered email, and you’ll receive instructions to reset it.
              </p>
            </li>
            <li className="mb-2">
              <strong className="text-gray-800 dark:text-stone-200">
                Can I use KeyWay on multiple devices?
              </strong>
              <p>
                Yes. Log in with the same account on different devices, and your
                library, notes, and progress will sync automatically.
              </p>
            </li>
          </ul>
        </div>

        {/* Content & Features */}
        <div className="mb-6">
          <h2 className="text-xl fontKanitRegular font-medium mb-2 text-gray-900 dark:text-stone-100">
            Content & Features
          </h2>
          <ul className="list-disc pl-5 fontAlbertSansRegular text-gray-700 dark:text-stone-300">
            <li className="mb-2">
              <strong className="text-gray-800 dark:text-stone-200">
                What kind of content is available?
              </strong>
              <p>
                KeyWay provides access to thousands of nonfiction audiobooks and
                summaries across business, self-improvement, wellness, finance,
                and more.
              </p>
            </li>
            <li className="mb-2">
              <strong className="text-gray-800 dark:text-stone-200">
                Can I listen offline?
              </strong>
              <p>
                Yes. Tap the download icon next to any audiobook or summary to
                make it available without internet access.
              </p>
            </li>
            <li className="mb-2">
              <strong className="text-gray-800 dark:text-stone-200">
                How often is new content added?
              </strong>
              <p>
                We add fresh audiobooks and summaries every week, so you’ll
                always have something new to explore.
              </p>
            </li>
            <li className="mb-2">
              <strong className="text-gray-800 dark:text-stone-200">
                Can I highlight or save notes?
              </strong>
              <p>
                Absolutely. You can highlight key ideas or add personal notes,
                which are saved in your Notes section.
              </p>
            </li>
          </ul>
        </div>

        {/* Subscription & Payments */}
        <div className="mb-6">
          <h2 className="text-xl fontKanitRegular font-medium mb-2 text-gray-900 dark:text-stone-100">
            Subscription & Payments
          </h2>
          <ul className="list-disc pl-5 fontAlbertSansRegular text-gray-700 dark:text-stone-300">
            <li className="mb-2">
              <strong className="text-gray-800 dark:text-stone-200">
                Is there a free version of the app?
              </strong>
              <p>
                Yes, KeyWay offers a free trial with limited access. You can
                upgrade to a premium plan for full access to all our content and
                features.
              </p>
            </li>
            <li className="mb-2">
              <strong className="text-gray-800 dark:text-stone-200">
                How do I subscribe to KeyWay?
              </strong>
              <p>
                Simply tap the <em>Subscribe</em> button in the app. You’ll be
                redirected to our website where you can choose a subscription
                plan and complete the payment.
              </p>
            </li>
            <li className="mb-2">
              <strong className="text-gray-800 dark:text-stone-200">
                How do I update or cancel my subscription?
              </strong>
              <p>
                To manage your subscription, visit our website through the app
                and log in to your account. From there, you can update your plan
                or cancel at any time.
              </p>
            </li>
            <li className="mb-2">
              <strong className="text-gray-800 dark:text-stone-200">
                What payment methods are accepted?
              </strong>
              <p>
                We accept major credit and debit cards on our website for
                subscriptions.
              </p>
            </li>
          </ul>
        </div>

        {/* Privacy & Security */}
        <div className="mb-6">
          <h2 className="text-xl fontKanitRegular font-medium mb-2 text-gray-900 dark:text-stone-100">
            Privacy & Security
          </h2>
          <ul className="list-disc pl-5 fontAlbertSansRegular text-gray-700 dark:text-stone-300">
            <li className="mb-2">
              <strong className="text-gray-800 dark:text-stone-200">
                Is my data secure?
              </strong>
              <p>
                Yes. KeyWay uses encryption and industry-standard security
                measures to protect your personal information.
              </p>
            </li>
            <li className="mb-2">
              <strong className="text-gray-800 dark:text-stone-200">
                Does KeyWay support multiple languages?
              </strong>
              <p>
                Our library is primarily in English for now, but we are
                expanding to include more languages in the future.
              </p>
            </li>
          </ul>
        </div>

        {/* Contact Us */}
        <div className="mb-4">
          <h2 className="text-xl font-medium fontKanitRegular mb-2 text-gray-900 dark:text-stone-100">
            Contact Us
          </h2>
          <p className="fontAlbertSansRegular text-gray-700 dark:text-stone-300">
            If you need additional help, feel free to reach our support team:
          </p>
          <ul className="list-none pl-0 fontAlbertSansRegular text-gray-700 dark:text-stone-300">
            <li className="mb-2">
              <strong className="text-gray-800 dark:text-stone-200">
                Email:
              </strong>{" "}
              Contact@businessyield.com
            </li>
            <li className="mb-2">
              <strong className="text-gray-800 dark:text-stone-200">
                Phone:
              </strong>{" "}
              +44 7424 607827
            </li>
            <li className="mb-2">
              <strong className="text-gray-800 dark:text-stone-200">
                Address:
              </strong>{" "}
              10 Mast Street, London, UK
            </li>
          </ul>
        </div>

        <div className="mt-6 text-center">
          <a
            href="mailto:Contact@businessyield.com"
            className="bg-blue-600 hover:bg-blue-700 dark:hover:bg-blue-500 text-white px-4 py-2 rounded-lg fontAlbertSansRegular inline-block"
          >
            Send us a Message
          </a>
        </div>
      </div>
    </div>
  );
};

export default ProfileHelpComp;
